// docs/39 FW6 — a walk on screen is the sim's route, square by square: one leg per square of
// `Moved.path` (four-way, so every leg runs along a row or a column), each lasting as long as the
// step cost, the figure turning to face each leg that goes east or west. Pure, so the keyframes
// can be tested without Phaser; `EffectPlayer` turns them into a tween chain.
import type { Effect, Tile } from '../sim/types';

export interface Leg {
  /** The square this leg ends on. */
  tile: Tile;
  /** How long the leg lasts (ms). */
  ms: number;
  /** Which way the figure faces for it: 1 east, -1 west, 0 keep (a leg north or south). */
  face: 1 | -1 | 0;
}

/** The squares a shove crosses from `from` to `to` (a straight line along one side, docs/39 FW4). */
export function shoveSquares(from: Tile, to: Tile): Tile[] {
  const dx = Math.sign(to.x - from.x), dy = Math.sign(to.y - from.y);
  const out: Tile[] = [];
  let x = from.x, y = from.y;
  for (let guard = 0; (x !== to.x || y !== to.y) && guard < 64; guard++) {
    if (x !== to.x) x += dx; else y += dy;
    out.push({ x, y });
  }
  return out;
}

/**
 * Where a moving effect starts and the legs it plays, or undefined for anything that does not
 * move a unit. `start` is the square the walk leaves — the sim's `from` when it says, else the
 * figure's own square. `ms` is the clip's whole length, shared out by step cost.
 */
export function legsOf(e: Effect, here: Tile, ms: number): { start: Tile; legs: Leg[] } | undefined {
  let start: Tile, squares: Tile[], costs: number[];
  if (e.kind === 'Moved') {
    start = e.from ?? here;
    squares = e.path;
    costs = e.costs && e.costs.length === e.path.length ? e.costs : e.path.map(() => 1);
  } else if (e.kind === 'Left') {
    // docs/118 PT14b: a run off the field, from where the sim says it began
    start = e.from;
    squares = e.path;
    costs = e.costs && e.costs.length === e.path.length ? e.costs : e.path.map(() => 1);
  } else if (e.kind === 'Displaced') {
    start = e.from;
    squares = shoveSquares(e.from, e.to);
    costs = squares.map(() => 1);
  } else return undefined;
  const total = costs.reduce((a, c) => a + Math.max(1, c), 0);
  let prev = start;
  const legs = squares.map((t, i) => {
    const face: Leg['face'] = t.x > prev.x ? 1 : t.x < prev.x ? -1 : 0;
    prev = t;
    return { tile: t, ms: total > 0 ? (ms * Math.max(1, costs[i]!)) / total : 0, face };
  });
  return { start, legs };
}

/** docs/79: where a body's figure stands (its feet: the bottom centre of its footprint) when its origin is `t`. */
export function feetPoint(t: Tile, size: number, cell: number): { x: number; y: number } {
  return { x: (t.x + size / 2) * cell, y: (t.y + size) * cell };
}

/**
 * The tween chain a walk plays (`EffectPlayer`): one keyframe per leg, from the feet point of one
 * footprint to the next, linear but for an eased last leg — for a body of any size, so a huge foe
 * walks as smoothly as a goblin.
 */
export function walkKeys(legs: readonly Leg[], size: number, cell: number): Array<{ x: number; y: number; ms: number; ease: 'Linear' | 'Quad.easeOut' }> {
  return legs.map((leg, i) => ({ ...feetPoint(leg.tile, size, cell), ms: Math.max(1, leg.ms), ease: i === legs.length - 1 ? 'Quad.easeOut' : 'Linear' }));
}

/** docs/79: how the board shakes when a huge body lands — none for one square, a little more each size up. */
export function thumpShake(size: number): { ms: number; intensity: number } | undefined {
  if (size < 2) return undefined;
  return { ms: size >= 3 ? 180 : 140, intensity: size >= 3 ? 0.007 : 0.004 };
}
